// /signin/check: "Check your inbox" + 6-digit code entry.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui';
import { useAuth } from '../../context/useAuth.jsx';
import CodeBoxes from './CodeBoxes.jsx';
import { AuthShell, FormMsg } from './AuthShell.jsx';

export default function CheckInbox() {
  const location = useLocation();
  const q = location.state?.masked || '';
  // Falls back gracefully when the router state is gone (e.g. a reload).
  const masked = /^[^\s<>]{1,64}@[^\s<>]{1,255}$/.test(q) ? q : 'your email address';
  const { refreshSession } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(45);

  useEffect(() => {
    if (left <= 0) return undefined;
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setMsg('');
    try {
      await axios.post('/auth/email/verify-code', { code }, { withCredentials: true });
      const session = await refreshSession({ force: true });
      navigate(session?.activated ? '/app' : '/welcome', { replace: true });
    } catch (err) {
      setBusy(false);
      const status = err?.response?.status;
      if (status === 410) {
        navigate('/signin/expired', { replace: true });
      } else if (status === 400) {
        const n = err.response.data?.attempts_left;
        setMsg(typeof n === 'number'
          ? `That code isn't right. ${n} ${n === 1 ? 'try' : 'tries'} left.`
          : "That code isn't right.");
        setCode('');
      } else {
        setMsg('Something went wrong. Please try again in a moment.');
      }
    }
  };

  return (
    <AuthShell
      aside={<><b>Opened the email on your phone?</b> Type the code here instead, and you'll be signed in on this device.</>}
    >
      <h1 className="text-2xl font-semibold text-gray-900">Check your inbox.</h1>
      <p className="text-sm text-gray-600 mt-2 mb-6">
        We've sent a sign-in link to <b className="text-gray-900">{masked}</b>. It works once, for the next 15 minutes.
      </p>
      <form onSubmit={submit} noValidate>
        <p className="text-sm text-gray-700 mb-3" id="codelabel">Or type the 6-digit code from the email</p>
        <CodeBoxes value={code} onChange={setCode} disabled={busy} />
        <FormMsg error>{msg}</FormMsg>
        <Button type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={code.length !== 6 || busy}>
          Continue
        </Button>
      </form>
      <div className="flex justify-between items-center mt-5 text-sm">
        <span className="text-gray-500">
          {left > 0
            ? <>Resend in <b className="text-gray-900">0:{String(left).padStart(2, '0')}</b></>
            : <Link className="underline" to="/login">Send a new link</Link>}
        </span>
        <Link className="underline" to="/login">Use a different email</Link>
      </div>
    </AuthShell>
  );
}
